<template>
  <div class="min-h-screen flex">
 
    <!-- Right Side - Login Form -->
    <div
      class="w-full lg:w-full flex items-center justify-center p-8 sm:p-12 bg-gray-50 dark:bg-gray-900"
    >
      <div class="w-full max-w-md space-y-8">
        <!-- Mobile Logo (visible only on mobile) -->

          <nuxt-link to="/">
            <img
              src="../../assets/images/logo-black.svg"
              alt="Academic Alerts"
              class="h-8 w-auto mx-auto mb-6"
            />
          </nuxt-link>

          <hr>

        <div class="text-center">
          <h2 class="text-3xl font-bold text-gray-900 dark:text-white">
            Sign in to your account
          </h2>
          <p class="mt-2 text-sm text-gray-600 dark:text-gray-400">
            Access your academic dashboard
          </p>
        </div>

        <!-- Rest of the form remains the same -->
        <div class="mt-8">
          <form @submit.prevent="handleLogin" class="space-y-6">
            <div class="space-y-5">
              <!-- Email Input -->
              <div>
                <label
                  for="email"
                  class="block text-sm font-medium text-gray-700 dark:text-gray-300"
                >
                  Email address
                </label>
                <div class="mt-1 relative">
                  <div
                    class="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none"
                  >
                    <Icon
                      name="heroicons:envelope"
                      class="h-5 w-5 text-gray-400"
                    />
                  </div>
                  <input
                    id="email"
                    v-model="formData.identifier"
                    type="email"
                    required
                    class="pl-10 block w-full rounded-lg border border-gray-300 dark:border-gray-600 shadow-sm py-3 px-4 placeholder-gray-400 focus:ring-2 focus:ring-primary/50 focus:border-primary dark:bg-gray-800 dark:text-white"
                    placeholder="Enter your email"
                  />
                </div>
              </div>

              <!-- Password Input -->
              <div>
                <label
                  for="password"
                  class="block text-sm font-medium text-gray-700 dark:text-gray-300"
                >
                  Password
                </label>
                <div class="mt-1 relative">
                  <div
                    class="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none"
                  >
                    <Icon
                      name="heroicons:lock-closed"
                      class="h-5 w-5 text-gray-400"
                    />
                  </div>
                  <input
                    id="password"
                    v-model="formData.password"
                    :type="showPassword ? 'text' : 'password'"
                    required
                    class="pl-10 block w-full rounded-lg border border-gray-300 dark:border-gray-600 shadow-sm py-3 px-4 placeholder-gray-400 focus:ring-2 focus:ring-primary/50 focus:border-primary dark:bg-gray-800 dark:text-white"
                    placeholder="Enter your password"
                  />
                  <button
                    type="button"
                    @click="showPassword = !showPassword"
                    class="absolute inset-y-0 right-0 pr-3 flex items-center"
                  >
                    <Icon
                      :name="
                        showPassword ? 'heroicons:eye-slash' : 'heroicons:eye'
                      "
                      class="h-5 w-5 text-gray-400 hover:text-gray-600"
                    />
                  </button>
                </div>
              </div>
            </div>

            <!-- Error Message -->
            <div
              v-if="error"
              class="bg-red-50 dark:bg-red-900/30 text-red-600 dark:text-red-400 text-sm rounded-lg p-3 text-center"
            >
              {{ error }}
            </div>

            <!-- Submit Button -->
            <button
              type="submit"
              :disabled="isLoading"
              class="relative w-full flex justify-center py-3 px-4 border border-transparent rounded-lg shadow-sm text-sm font-medium text-white bg-primary hover:bg-primary/90 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200 group"
            >
              <span class="absolute inset-y-0 left-0 flex items-center pl-3">
                <Icon
                  name="heroicons:lock-closed"
                  class="h-5 w-5 text-primary-light group-hover:text-white"
                />
              </span>
              <span class="flex items-center">
                <Icon
                  v-if="isLoading"
                  name="heroicons:arrow-path"
                  class="animate-spin h-5 w-5 mr-2"
                />
                {{ isLoading ? "Signing in..." : "Sign in" }}
              </span>
            </button>
          </form>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
const router = useRouter();
const { login } = useStrapiAuth();

const formData = ref({
  identifier: "",
  password: "",
});

const showPassword = ref(false);
const isLoading = ref(false);
const error = ref(null);

async function handleLogin() {
  error.value = null;
  isLoading.value = true;

  try {
    await login(formData.value);
    router.push("/dashboard");
  } catch (e) {
    error.value = "Invalid email or password";
  } finally {
    isLoading.value = false;
  }
}

// Reset error when form data changes
watch(formData, () => {
  error.value = null;
});
</script>

<style scoped>
@keyframes gradient {
  0% {
    opacity: 0.9;
  }
  50% {
    opacity: 0.95;
  }
  100% {
    opacity: 0.9;
  }
}

.animate-gradient {
  animation: gradient 8s ease infinite;
}

.animate-fade-in {
  animation: fadeIn 1s ease-out;
}

@keyframes fadeIn {
  from {
    opacity: 0;
    transform: translateY(10px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}
</style>
